import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Input } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import { type ComplexLoginFormData } from '@/sections/asset/components/tabs/credentials/credentialForms/types';
import { TOTP_SECRET_INPUT_KEY } from '@/sections/asset/components/tabs/credentials/totpSecret';
import { TOTPSecretInput } from '@/sections/asset/components/tabs/credentials/TOTPSecretInput';

interface ComplexLoginFormProps {
  formData: ComplexLoginFormData;
  onChange: React.Dispatch<React.SetStateAction<ComplexLoginFormData>>;
  /**
   * Which slice of the form to render. The Complex-Login form is split across
   * two sequential screens in both hosts (asset drawer + seed wizard):
   *   - `'basics'` → Label, Login URL, Recorder hint
   *   - `'inputs'` → the Login Inputs key/value editor, the TOTP secret input,
   *                  and the grey recorder note
   *   - `'all'`    → everything in the original single-screen order (back-compat
   *                  default for any caller that doesn't split)
   */
  section?: 'all' | 'basics' | 'inputs';
}

interface ComplexLoginSectionProps {
  formData: ComplexLoginFormData;
  onChange: React.Dispatch<React.SetStateAction<ComplexLoginFormData>>;
}

export const ComplexLoginForm: React.FC<ComplexLoginFormProps> = ({
  formData,
  onChange,
  section = 'all',
}) => {
  // Each field block is rendered exactly once below from the shared helpers, so
  // there is no field-markup duplication across sections. The `'all'` arm lays
  // them out in the ORIGINAL single-screen order (Label, Login URL, Login
  // Inputs, TOTP, Recorder hint, note) for back-compat; the split arms group
  // them into the two sequential screens.
  return (
    <div className="space-y-4">
      {section === 'all' && (
        <>
          <LabelField formData={formData} onChange={onChange} />
          <LoginUrlField formData={formData} onChange={onChange} />
          <LoginInputsField formData={formData} onChange={onChange} />
          <TotpField formData={formData} onChange={onChange} />
          <RecorderHintField formData={formData} onChange={onChange} />
          <RecorderNote />
        </>
      )}
      {section === 'basics' && (
        <>
          <LabelField formData={formData} onChange={onChange} />
          <LoginUrlField formData={formData} onChange={onChange} />
          <RecorderHintField formData={formData} onChange={onChange} />
        </>
      )}
      {section === 'inputs' && (
        <>
          <LoginInputsField formData={formData} onChange={onChange} />
          <TotpField formData={formData} onChange={onChange} />
          <RecorderNote />
        </>
      )}
    </div>
  );
};

const LabelField: React.FC<ComplexLoginSectionProps> = ({
  formData,
  onChange,
}) => (
  <Input
    label="Label"
    name="label"
    value={formData.label}
    onChange={e => onChange(prev => ({ ...prev, label: e.target.value }))}
    placeholder="e.g. acme-prod-admin"
    required
    data-testid="discover-login-label-input"
  />
);

const LoginUrlField: React.FC<ComplexLoginSectionProps> = ({
  formData,
  onChange,
}) => (
  <Input
    label="Login URL"
    name="loginUrl"
    value={formData.loginUrl}
    onChange={e => onChange(prev => ({ ...prev, loginUrl: e.target.value }))}
    placeholder="https://app.example.com/login"
    required
    data-testid="discover-login-url-input"
  />
);

const RecorderHintField: React.FC<ComplexLoginSectionProps> = ({
  formData,
  onChange,
}) => (
  <div>
    <label
      htmlFor="discover-login-hint"
      className="text-chariot-text-primary mb-2 block text-sm font-medium"
    >
      Recorder hint (optional)
    </label>
    <p className="text-chariot-text-secondary mb-2 text-sm">
      {`Free-text guidance for the recorder agent — useful when the page has multiple login paths. E.g. "Use the Okta SSO button, not the username/password form" or "Click 'Continue with SAML'".`}
    </p>
    <textarea
      id="discover-login-hint"
      rows={2}
      value={formData.hint}
      onChange={e => onChange(prev => ({ ...prev, hint: e.target.value }))}
      placeholder="e.g. Use the Okta SSO button"
      className="border-chariot-stroke bg-chariot-background-primary w-full rounded-xs border p-1.5 pl-3"
      data-testid="discover-login-hint-input"
    />
  </div>
);

const LoginInputsField: React.FC<ComplexLoginSectionProps> = ({
  formData,
  onChange,
}) => {
  const updateInput = (index: number, field: 'key' | 'value', val: string) => {
    onChange(prev => {
      const next = [...prev.inputs];
      next[index] = { ...next[index], [field]: val };
      return { ...prev, inputs: next };
    });
  };

  const visibleInputs = formData.inputs
    .map((input, index) => ({ input, index }))
    .filter(({ input }) => input.key.trim() !== TOTP_SECRET_INPUT_KEY);

  return (
    <div>
      <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
        Login Inputs
      </label>
      <p className="text-chariot-text-secondary mb-3 text-sm">
        {`Any key/value pair the login flow needs (username, password, OTP backup code, etc.). The agent sees only the keys — values are substituted at sidecar-call time and never appear in the recorded recipe or conversation log.`}
      </p>
      {visibleInputs.map(({ input, index }) => (
        <div key={index} className="mb-2 flex gap-2">
          <input
            type="text"
            placeholder="Key (e.g. username)"
            value={input.key}
            onChange={e => updateInput(index, 'key', e.target.value)}
            className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-xs border p-1.5 pl-3"
            data-testid={`discover-login-input-key-${index}`}
          />
          <input
            type="password"
            placeholder="Value"
            value={input.value}
            onChange={e => updateInput(index, 'value', e.target.value)}
            className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-xs border p-1.5 pl-3"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-testid={`discover-login-input-value-${index}`}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(prev => ({
                ...prev,
                inputs: prev.inputs.filter((_, i) => i !== index),
              }));
            }}
            aria-label="Remove input"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          onChange(prev => ({
            ...prev,
            inputs: [...prev.inputs, { key: '', value: '' }],
          }));
        }}
        data-testid="add-discover-login-input"
      >
        <PlusIcon className="size-4" />
        Add Input
      </Button>
    </div>
  );
};

const TotpField: React.FC<ComplexLoginSectionProps> = ({
  formData,
  onChange,
}) => {
  // The TOTP secret rides in the same inputs array but is edited via a
  // dedicated control (paste OR QR upload). Hide it from the generic KV
  // editor so the operator doesn't accidentally double-edit it.
  const totpSecret =
    formData.inputs.find(i => i.key.trim() === TOTP_SECRET_INPUT_KEY)?.value ??
    '';
  const setTOTPSecret = (secret: string) => {
    onChange(prev => {
      const filtered = prev.inputs.filter(
        i => i.key.trim() !== TOTP_SECRET_INPUT_KEY
      );
      const next = secret
        ? [...filtered, { key: TOTP_SECRET_INPUT_KEY, value: secret }]
        : filtered;
      return { ...prev, inputs: next };
    });
  };

  return <TOTPSecretInput value={totpSecret} onChange={setTOTPSecret} />;
};

const RecorderNote: React.FC = () => (
  <div className="bg-chariot-background-secondary rounded-lg p-3">
    <p className="text-chariot-text-secondary text-sm">
      <strong>Note:</strong>{' '}
      {`Submitting starts a recorder agent. You'll watch each step appear here as it drives a browser through the login. If it succeeds, the recipe is verified by deterministic replay and saved automatically.`}
    </p>
  </div>
);
